import { installAppActionBreadcrumbs, truncateAppActionValue } from '@goldcast/logger';
import { AnalyticsBrowser, EventProperties, SegmentEvent } from '@segment/analytics-next';

import loggerService from './services/logger.service';

import { getEnv } from '@/constants';
import { getCurrentUserId } from '@/utils/storeAccess';

export const SEGMENT_EVENTS = Object.freeze({
  route_change: 'Route change',
  magic_link_verified: 'magic_link_verified',
  agora_user: 'agora-user',
  lite_mode_force_show_tooltip: 'lite_mode_force_show_tooltip',
  event_enter: 'event_enter',
  sidebar_tab_opened: 'sidebar_tab_opened',
  agenda_page_visited: 'agenda_page_visited',
  poll_answered: 'poll_answered',
  user_profile_updated: 'user_profile_updated',
  settings_modal_open: 'settings_modal_open',
  chat_messaged_send: 'chat_messaged_send',
  speaker_page_visited: 'speaker_page_visited',
  user_profile_show: 'user_profile_show',
  catch_me_up_feedback: 'catch_me_up_feedback'
});

export const UNUSED_SEGMENT_EVENTS = [
  SEGMENT_EVENTS.route_change,
  SEGMENT_EVENTS.magic_link_verified,
  SEGMENT_EVENTS.agora_user,
  SEGMENT_EVENTS.lite_mode_force_show_tooltip,
  SEGMENT_EVENTS.event_enter,
  SEGMENT_EVENTS.sidebar_tab_opened,
  SEGMENT_EVENTS.agenda_page_visited,
  SEGMENT_EVENTS.poll_answered,
  SEGMENT_EVENTS.user_profile_updated,
  SEGMENT_EVENTS.settings_modal_open,
  SEGMENT_EVENTS.speaker_page_visited,
  SEGMENT_EVENTS.user_profile_show
];

const ANALYTICS_CONFIG = [
  {
    writeKey: getEnv('VUE_APP_SEGMENT_KEY'),
    cdnURL: getEnv('VUE_APP_ANALYTICS_CDN_URL')
  },
  {
    integrations: getEnv('VUE_APP_SEGMENT_INTEGRATION_CONFIG') as any
  }
] as any;

// Install delegated capture-phase listeners for app_action breadcrumbs.
// Non-blocking, safe (no preventDefault/stopPropagation), and coalesced.
installAppActionBreadcrumbs(loggerService);

let analyticsClient: AnalyticsBrowser | null = null;
let userToIdentify: { id: string; name: string; email: string } | null = null;
const trackingBacklog: { eventName: SegmentEvent | string; properties: any }[] = [];
const MAX_RETRIES = 5;
let retries = 0;
let isAnalyticsPackageLoaded = false;
// Consent gate. Segment is not loaded here: loadAnalytics() runs on the first
// identify() instead, so a session that is never identified (unauthenticated
// visitor, bounce, opted-out) never loads the SDK and never reaches Segment.
let trackingAllowed = false;

function initialize() {
  if (trackingAllowed) {
    return;
  }
  trackingAllowed = true;
  // identify() may have already run and buffered a user; load for it now.
  if (userToIdentify) {
    loadAnalytics();
  }
}

function loadAnalytics() {
  analyticsClient = AnalyticsBrowser.load(ANALYTICS_CONFIG[0], ANALYTICS_CONFIG[1]);
  analyticsClient
    .then(() => {
      isAnalyticsPackageLoaded = true;
      loggerService.info('segment_debug', { operation: 'load_success' });
      if (userToIdentify) {
        identify(userToIdentify.id, { name: userToIdentify.name, email: userToIdentify.email });
      }
      const backlogCount = trackingBacklog.length;
      trackingBacklog.forEach(payload => {
        track(payload.eventName, payload.properties);
      });
      if (backlogCount > 0) {
        loggerService.info('segment_debug', { operation: 'backlog_cleared', count: backlogCount });
      }
    })
    .catch(() => {
      isAnalyticsPackageLoaded = false;
      const exponentialDelay = Math.pow(2, retries - 1);
      loggerService.info('segment_debug', { operation: 'load_retry', delaySeconds: exponentialDelay });
      retry();
    });
}

function retry() {
  if (retries < MAX_RETRIES) {
    retries++;
    const exponentialDelay = Math.pow(2, retries - 1) * 1000; // 1s, 2s, 4s, 8s etc
    setTimeout(() => {
      loadAnalytics();
    }, exponentialDelay);
  } else {
    loggerService.error('segment_debug', { operation: 'load_failed', retries });
  }
}

function logActionBreadcrumb(eventName: string | SegmentEvent) {
  try {
    loggerService.info('app_action', {
      action: String(eventName),
      route: truncateAppActionValue(window.location?.pathname || '')
    });
  } catch {
    // Breadcrumb logging must never change the existing analytics behavior.
  }
}

function track(eventName: string | SegmentEvent, properties?: EventProperties | undefined): Promise<any> | undefined {
  logActionBreadcrumb(eventName);

  if (UNUSED_SEGMENT_EVENTS.includes(eventName as any)) {
    loggerService.info('segment_debug', { operation: 'event_skipped', event: String(eventName) });
    return Promise.resolve();
  }

  if (!trackingAllowed) {
    return Promise.resolve();
  }

  if (!analyticsClient || !isAnalyticsPackageLoaded) {
    trackingBacklog.push({ eventName, properties });
    loggerService.info('segment_debug', { operation: 'track_backlogged', backlogSize: trackingBacklog.length });
    return Promise.resolve();
  }
  const isUserIdentified = getCurrentUserId();
  if (isUserIdentified) {
    return analyticsClient.track(eventName, properties);
  }
  loggerService.info('segment_debug', { operation: 'user_not_identified', event: String(eventName) });
  return Promise.resolve();
}

function identify(userId: string, traits: { name: string; email: string }) {
  // Buffer first: every path below replays this once the SDK is ready, so the
  // identify call is never lost and is always the first event Segment sees.
  userToIdentify = { id: userId, name: traits.name, email: traits.email };

  if (!trackingAllowed) {
    return Promise.resolve();
  }

  if (!analyticsClient) {
    loadAnalytics();
    return Promise.resolve();
  }

  if (!isAnalyticsPackageLoaded) {
    loggerService.info('segment_debug', { operation: 'identify_backlogged', userId });
    return Promise.resolve();
  }

  analyticsClient.identify(userId, { name: traits.name, email: traits.email });
  userToIdentify = null;
  loggerService.info('segment_debug', { operation: 'identify_success', userId });
  return Promise.resolve();
}

function reset() {
  // Clear the identified user only. Don't clear `trackingAllowed`: consent is
  // decided once at bootstrap, so flipping it here would permanently disable
  // tracking for the rest of the session.
  userToIdentify = null;
  if (!analyticsClient || !isAnalyticsPackageLoaded) {
    loggerService.info('segment_debug', { operation: 'reset_unavailable' });
    return Promise.resolve();
  }
  analyticsClient.reset();
  return;
}

const analytics = {
  initialize,
  identify,
  track,
  reset
};

export default analytics;
